import { formatAddress } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import { useGetAssetsBySmaQuery } from '@core/redux/query';
import { useOrderNumbers } from '@elements/components/ElementDialog/useOrderNumbers';
import { elementSelectors } from '@elements/redux';
import { pointElementSelectors } from '@elements/redux/point-element.slice';
import { PointElement, SplineElement } from '@elements/types';
import { Autocomplete, ListItem, ListItemText, Stack, TextField, Typography } from '@mui/material';
import { ExportTarget } from '@projects/types';
import { viewer3dActions } from '@viewer3D/redux/actions';
import { viewer3dSelectors } from '@viewer3D/redux/selectors';
import { useState } from 'react';
import { useSelector } from 'react-redux';

export const SMASearch = () => {
  const [smaNumberSearch, setSmaNumberSearch] = useState('');
  const [elementSearch, setElementSearch] = useState('');
  const [smaNumber, setSmaNumber] = useState<{ name: string; smaNumber: string } | null>(null);
  const [element, setElement] = useState<PointElement | SplineElement | null>(null);
  const { data: smaAssets } = useGetAssetsBySmaQuery({
    orderNumber: smaNumber?.smaNumber,
    assetTypes: ['POINT_ELEMENT', 'SPLINE_ELEMENT'],
  });

  const smaPointElements = new Set(smaAssets?.pointElementIds);
  const smaSplineElements = new Set(smaAssets?.splineElementIds);

  const dispatch = useAppDispatch();

  const selectElementById = (id: string) => {
    dispatch(viewer3dActions.setSelectedElementIds([id]));
  };

  const resetElement = () => {
    setElement(null);
    setElementSearch('');
  };

  const project = useSelector(viewer3dSelectors.selectedProject);
  const { orderNumbers } = useOrderNumbers();

  const smaNumbers = orderNumbers?.flatMap((sma) => {
    return sma.networkNodeMappings.map((mapping) => {
      return {
        name: mapping.name,
        smaNumber: sma.id,
      };
    });
  });

  const pointElements = useSelector(pointElementSelectors.selectAll);
  const filteredPointElements = pointElements.filter((pe) => smaPointElements.has(pe.id));

  const splineElements = useSelector(elementSelectors.selectAll);
  const filteredSplineElements = splineElements.filter((se) => smaSplineElements.has(se.id));

  const filteredElements = [...filteredPointElements, ...filteredSplineElements];

  if (project?.exportTarget !== ExportTarget.Telekom) {
    return null;
  }

  return (
    <Stack gap={2}>
      <Autocomplete
        options={smaNumbers ?? []}
        isOptionEqualToValue={(option, value) => option?.smaNumber === value?.smaNumber}
        getOptionLabel={(option) => `${option.name} - ${option.smaNumber}`}
        value={smaNumber}
        renderOption={(props, option, i) => (
          <ListItem
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            key={`${option?.name}-${option?.smaNumber}-${i.index}`}
            disableGutters
          >
            <ListItemText>{option?.name}</ListItemText>
            <Typography variant="body2" color="secondary">
              {option?.smaNumber}
            </Typography>
          </ListItem>
        )}
        componentsProps={{ paper: { elevation: 24 } }}
        noOptionsText="No available element names found"
        inputValue={smaNumberSearch}
        onChange={(_, nextOption, reason) => {
          if (reason === 'selectOption') {
            setSmaNumber(nextOption as { name: string; smaNumber: string });
            resetElement();
          }
          if (reason === 'clear') {
            setSmaNumber(null);
            resetElement();
          }
        }}
        onInputChange={(e, newInputValue) => {
          if (!e || e.type === 'blur') return; // avoid erase of input value on initial rendering
          setSmaNumberSearch(newInputValue);
        }}
        renderInput={(renderParams) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <TextField {...renderParams} label="Search SMA Number" variant="outlined" fullWidth />
        )}
      />
      <Autocomplete
        options={filteredElements ?? []}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionLabel={(option) =>
          option?.name ? `${option.name} - ${formatAddress(option.address)}` : ''
        }
        renderOption={(props, option) => (
          <ListItem
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            key={option?.id}
            disableGutters
          >
            <ListItemText>{option?.name}</ListItemText>
            <Typography variant="body2" color="secondary">
              {formatAddress(option.address)}
            </Typography>
          </ListItem>
        )}
        componentsProps={{ paper: { elevation: 24 } }}
        noOptionsText="No available element names found"
        value={element}
        inputValue={elementSearch}
        onChange={(_, nextOption, reason) => {
          if (reason === 'selectOption') {
            setElement(nextOption as PointElement | SplineElement);
            selectElementById(nextOption?.id ?? '');
          }
          if (reason === 'clear') {
            resetElement();
          }
        }}
        onInputChange={(e, newInputValue) => {
          if (!e || e.type === 'blur') return; // avoid erase of input value on initial rendering
          setElementSearch(newInputValue);
        }}
        renderInput={(renderParams) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <TextField {...renderParams} label="Elements" variant="outlined" fullWidth />
        )}
      />
    </Stack>
  );
};
